import { Tooltip } from "@/base";
import { useApiServiceContext } from "@/contexts";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Chip,
    cn,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Spinner,
    useDisclosure,
} from "@heroui/react";
import toast from "react-hot-toast";
import {
    ApplicantDocumentStatus,
    DangerousApplicantDocumentStatuses,
    type ApplicantDocument,
} from "@sr-partner-portal/common";
import {
    IconAlertTriangle,
    IconDeviceFloppy,
    IconDotsVertical,
    IconFileText,
    IconShieldSearch,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
    type FC,
    useCallback,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
    useMemo,
} from "react";

export interface DocumentProps {
    document?: ApplicantDocument;
    onMarkRead?: (variables: { id: number }) => void;
    onMarkUnread?: (variables: { id: number }) => void;
    onRename?: (variables: { id: number; name: string }) => void;
    onRemove?: (variables: { id: number }) => void;
    onDownload?: (variables: { id: number }) => void;
    onGetPreviewUrl: (variables: { id: number }) => Promise<string>;
    isLoading?: boolean;
    openPreviewInNewTab?: boolean;
    shareLinkToken?: string;
    hideUnreadTag?: boolean;
}

const getFileNameWithoutExtension = (fileName: string) => {
    return fileName?.split(".").slice(0, -1).join(".");
};

const DocumentPreviewCard = ({
    document,
    previewUrl,
}: {
    document: ApplicantDocument;
    previewUrl?: string;
}) => {
    return (
        <div className="flex h-full w-full items-center justify-center">
            {document.type === "PDF" && previewUrl ? (
                <iframe
                    title={document.name}
                    src={previewUrl}
                    className="h-full w-full"
                />
            ) : previewUrl ? (
                <img
                    src={previewUrl}
                    alt="Document Preview"
                    className="max-w-full"
                />
            ) : (
                <Spinner />
            )}
        </div>
    );
};

const DocumentPreview = ({ document }: { document: ApplicantDocument }) => {
    const apiService = useApiServiceContext();

    const previewUrlQuery = useQuery({
        queryKey: ["documentPreview", document.id],

        queryFn: () =>
            apiService.applicantDocuments.getPreviewUrlAsync(document.id),
    });

    return (
        <DocumentPreviewCard
            document={document}
            previewUrl={previewUrlQuery.data?.previewUrl}
        />
    );
};

const ShareLinkDocumentPreview = ({
    document,
    token,
}: {
    document: ApplicantDocument;
    token: string;
}) => {
    const apiService = useApiServiceContext();

    const previewUrlQuery = useQuery({
        queryKey: ["documentPreview", document.id, token],

        queryFn: () =>
            apiService.applicantDocuments.getPreviewUrlByShareLinkToken({
                id: document.id,
                token,
            }),
    });

    return (
        <DocumentPreviewCard
            document={document}
            previewUrl={previewUrlQuery.data?.previewUrl}
        />
    );
};

const RenameModal = ({
    isDocumentFormOpen,
    onDocumentFormOpenChange,
    document,
    value,
    setValue,
    onRename,
}: {
    isDocumentFormOpen: boolean;
    onDocumentFormOpenChange: (isOpen: boolean) => void;
    document: ApplicantDocument;
    value: string;
    setValue: (value: string) => void;
    onRename: (variables: { id: number; name: string }) => void;
}) => {
    const inputRef = useRef(null);

    const extension = document.name.split(".").pop();
    const documentName = getFileNameWithoutExtension(document.name);
    const isDirty = value !== documentName;

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Enter" && isDirty) {
                event.preventDefault();
                onDocumentFormOpenChange(false);
                onRename({
                    id: document.id,
                    name: `${value}.${extension}`,
                });
            }
        },
        [
            document.id,
            extension,
            isDirty,
            value,
            onRename,
            onDocumentFormOpenChange,
        ],
    );

    useEffect(() => {
        if (isDocumentFormOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isDocumentFormOpen]);

    return (
        <Modal
            isOpen={isDocumentFormOpen}
            onOpenChange={onDocumentFormOpenChange}
            disableAnimation
            hideCloseButton={isDirty}
            isDismissable={!isDirty}
        >
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            Dokument umbenennen
                        </ModalHeader>
                        <ModalBody>
                            <Input
                                ref={inputRef}
                                label="Name"
                                value={value}
                                onValueChange={setValue}
                                endContent={`.${extension}`}
                                onKeyDown={handleKeyDown}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onPress={() => {
                                    onClose();
                                    setValue(
                                        getFileNameWithoutExtension(
                                            document.name,
                                        ),
                                    );
                                }}
                            >
                                {isDirty ? "Verwerfen" : "Abbrechen"}
                            </Button>
                            <Button
                                color="primary"
                                onPress={() => {
                                    onClose();
                                    onRename({
                                        id: document.id,
                                        name: `${value}.${extension}`,
                                    });
                                }}
                                startContent={
                                    <IconDeviceFloppy stroke={1.5} size={20} />
                                }
                                isDisabled={!isDirty}
                            >
                                Speichern
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

const DocumentIcon = ({
    thumbnailUrl,
    isLoading,
    documentStatus,
}: {
    thumbnailUrl?: string;
    isLoading?: boolean;
    documentStatus?: keyof typeof ApplicantDocumentStatus;
}) => {
    if (isLoading) {
        return <Spinner />;
    }

    if (documentStatus === ApplicantDocumentStatus.NEEDS_SCAN) {
        return (
            <Tooltip content="Dieses Dokument wird derzeit auf Sicherheit überprüft.">
                <IconShieldSearch
                    stroke={1.5}
                    size={48}
                    className="m-auto outline-none"
                />
            </Tooltip>
        );
    }

    if (
        documentStatus === ApplicantDocumentStatus.SCAN_FAILED ||
        documentStatus === ApplicantDocumentStatus.INFECTED
    ) {
        return (
            <Tooltip content="Dieses Dokument wurde als unsicher eingestuft und kann nicht angezeigt werden.">
                <IconAlertTriangle
                    stroke={1.5}
                    size={48}
                    className="m-auto text-danger outline-none"
                />
            </Tooltip>
        );
    }

    if (!thumbnailUrl) {
        return <IconFileText stroke={1.5} size={48} className="m-auto" />;
    }

    return null;
};

export const Document: FC<DocumentProps> = ({
    document,
    isLoading,
    openPreviewInNewTab = true,
    shareLinkToken,
    hideUnreadTag,
    onMarkRead,
    onMarkUnread,
    onRename,
    onRemove,
    onDownload,
    onGetPreviewUrl,
}) => {
    const apiService = useApiServiceContext();
    const {
        isOpen: isDocumentFormOpen,
        onOpen: onDocumentFormOpen,
        onOpenChange: onDocumentFormOpenChange,
    } = useDisclosure();
    const {
        isOpen: isPreviewOpen,
        onOpen: onPreviewOpen,
        onOpenChange: onPreviewOpenChange,
    } = useDisclosure();
    const {
        isOpen: isRemoveConfirmationOpen,
        onOpen: onRemoveConfirmationOpen,
        onClose: onRemoveConfirmationClose,
        onOpenChange: onRemoveConfirmationOpenChange,
    } = useDisclosure();
    const [previewIsLoading, setPreviewIsLoading] = useState(false);
    const [value, setValue] = useState(
        getFileNameWithoutExtension(document.name),
    );

    const documentIsUnsafe = useMemo(
        () => DangerousApplicantDocumentStatuses.includes(document.status),
        [document],
    );

    const thumbnailUrlQuery = useQuery({
        queryKey: ["documentThumbnail", document.id, shareLinkToken],

        queryFn: () => {
            if (shareLinkToken) {
                return apiService.applicantDocuments.getThumbnailUrlByShareLinkToken(
                    {
                        id: document.id,
                        token: shareLinkToken,
                    },
                );
            }

            return apiService.applicantDocuments.getThumbnailUrlAsync(
                document.id,
            );
        },

        enabled: !!document?.id,
    });

    const handlePreviewOpen = useCallback(async () => {
        if (!openPreviewInNewTab) {
            onPreviewOpen();
            return;
        }

        if (previewIsLoading || !onGetPreviewUrl) {
            return;
        }

        setPreviewIsLoading(true);

        try {
            const previewUrl = await onGetPreviewUrl(document);

            if (previewUrl) {
                window.open(previewUrl, "_blank")?.focus();
            }
        } catch {
            toast.error("Vorschau konnte nicht geöffnet werden.");
        } finally {
            setPreviewIsLoading(false);
        }
    }, [
        document,
        onGetPreviewUrl,
        onPreviewOpen,
        openPreviewInNewTab,
        previewIsLoading,
    ]);

    useEffect(() => {
        if (!document.thumbnailPath || thumbnailUrlQuery?.data?.thumbnailUrl) {
            return;
        }

        thumbnailUrlQuery.refetch();
    }, [
        thumbnailUrlQuery.refetch,
        document.thumbnailPath,
        thumbnailUrlQuery?.data?.thumbnailUrl,
    ]);

    const thumbnailUrl = documentIsUnsafe
        ? undefined
        : thumbnailUrlQuery?.data?.thumbnailUrl;

    return (
        // aspect ration w: 8, h: 11
        <div className="group relative pb-[calc(11/8*100%)]">
            <RenameModal
                isDocumentFormOpen={isDocumentFormOpen}
                onDocumentFormOpenChange={onDocumentFormOpenChange}
                document={document}
                value={value}
                setValue={setValue}
                onRename={onRename}
            />
            <Modal
                isOpen={isPreviewOpen}
                onOpenChange={onPreviewOpenChange}
                size="4xl"
                className="h-[90%]"
                hideCloseButton={true}
                shouldBlockScroll={true}
                classNames={{
                    body: "p-0",
                }}
                disableAnimation
            >
                <ModalContent>
                    <ModalBody>
                        {shareLinkToken ? (
                            <ShareLinkDocumentPreview
                                document={document}
                                token={shareLinkToken}
                            />
                        ) : (
                            <DocumentPreview document={document} />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Modal
                isOpen={isRemoveConfirmationOpen}
                onOpenChange={onRemoveConfirmationOpenChange}
                isDismissable={false}
                disableAnimation
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Dokument löschen
                    </ModalHeader>
                    <ModalBody>
                        Möchten Sie dieses Dokument wirklich löschen?
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={onRemoveConfirmationClose}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            type="button"
                            onPress={() => onRemove(document)}
                            isDisabled={isLoading}
                            isLoading={isLoading}
                            color="danger"
                        >
                            Löschen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Card
                isPressable={!documentIsUnsafe}
                as="div"
                onPress={handlePreviewOpen}
                shadow="none"
                disableRipple={true}
                isDisabled={isLoading}
                classNames={{
                    base: cn(
                        "absolute w-full h-full border border-default-200 bg-default-100",
                        thumbnailUrl ? "bg-cover bg-center" : "",
                    ),
                    body: "justify-center",
                }}
                style={
                    thumbnailUrl
                        ? {
                              backgroundImage: `url(${thumbnailUrl})`,
                          }
                        : {}
                }
            >
                <CardHeader className="pb-0">
                    <div className="flex h-10 w-full">
                        {!isLoading && (
                            <>
                                {!document.readAt && !hideUnreadTag && (
                                    <Chip color="danger">Neu</Chip>
                                )}
                                <Dropdown>
                                    <DropdownTrigger>
                                        <Button
                                            size="sm"
                                            className={cn(
                                                "ml-auto",
                                                thumbnailUrl &&
                                                    "opacity-0 group-hover:opacity-100",
                                            )}
                                            variant="faded"
                                            isIconOnly={true}
                                        >
                                            <IconDotsVertical
                                                size={18}
                                                stroke={1.5}
                                            />
                                        </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu aria-label="Static Actions">
                                        {!documentIsUnsafe && (
                                            <DropdownItem
                                                key="preview"
                                                onPress={handlePreviewOpen}
                                            >
                                                Vorschau öffnen
                                            </DropdownItem>
                                        )}
                                        {onMarkUnread &&
                                            onMarkRead &&
                                            !documentIsUnsafe &&
                                            (document.readAt ? (
                                                <DropdownItem
                                                    key="mark-unread"
                                                    onPress={() =>
                                                        onMarkUnread(document)
                                                    }
                                                >
                                                    Als ungelesen markieren
                                                </DropdownItem>
                                            ) : (
                                                <DropdownItem
                                                    key="mark-read"
                                                    onPress={() =>
                                                        onMarkRead(document)
                                                    }
                                                >
                                                    Als gelesen markieren
                                                </DropdownItem>
                                            ))}
                                        {onRename && !documentIsUnsafe && (
                                            <DropdownItem
                                                key="edit"
                                                onPress={onDocumentFormOpen}
                                            >
                                                Umbenennen
                                            </DropdownItem>
                                        )}
                                        {onDownload && !documentIsUnsafe && (
                                            <DropdownItem
                                                key="download"
                                                onPress={() =>
                                                    onDownload(document)
                                                }
                                            >
                                                Herunterladen
                                            </DropdownItem>
                                        )}
                                        {onRemove && (
                                            <DropdownItem
                                                key="delete"
                                                className="text-danger"
                                                color="danger"
                                                onPress={
                                                    onRemoveConfirmationOpen
                                                }
                                            >
                                                Löschen
                                            </DropdownItem>
                                        )}
                                    </DropdownMenu>
                                </Dropdown>
                            </>
                        )}
                    </div>
                </CardHeader>
                <CardBody>
                    <DocumentIcon
                        isLoading={isLoading || thumbnailUrlQuery.isLoading}
                        thumbnailUrl={thumbnailUrl}
                        documentStatus={document.status}
                    />
                </CardBody>
                <CardFooter className="bg-default-100">
                    <div className="flex w-full items-end">
                        <div className="truncate">{document?.name}</div>
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
};
